/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
const STRING_DASHERIZE_REGEXP = /[ _]/g;
const STRING_DECAMELIZE_REGEXP = /([a-z\d])([A-Z])/g;
const STRING_CAMELIZE_REGEXP = /(-|_|\.|\s)+(.)?/g;
const STRING_UNDERSCORE_REGEXP_1 = /([a-z\d])([A-Z]+)/g;
const STRING_UNDERSCORE_REGEXP_2 = /-|\s+/g;
export const STRING_LOWERCASE_REGEXP = /[a-z]/m;
export const STRING_UPPERCASE_REGEXP = /[A-Z]/m;
export const STRING_NUMBER_REGEXP = /\d/m;
export const STRING_SPECIALCHAR_REGEXP = /[-+_!@#$%^&*., ?]/m;

export const NO_HTML_ESCAPING_CHARACTERS_REGEX = /^[^<>"]+$/;

/**
 * Ensure all characters are within the Windows-1252 Charset (Lab Sys DB requirements).
 */
export const WINDOWS_1252_CHARSET = /^[\x00-\x7F\xA0-\xFF]*$/u;

/**
 * Minimal validation based on https://nzpost.co.nz/sites/nz/files/2021-10/nzpost-data-injection-standards.pdf,
 * such that it can be applied to any address field other than post code
 */
export const VALID_ADDRESS_REGEX = /^[\p{L}0-9 .,-/&()]*$/u;

/**
 * Allow names like "Marie-Élise L'Heureux"
 */
export const VALID_NAME_REGEX = /^[\p{L} '-]*$/u;

/**
 * Allow anything other than certain HTML/JavaScript special characters
 */
export const VALID_COMPANY_NAME_REGEX = NO_HTML_ESCAPING_CHARACTERS_REGEX;

/**
 * Four numeric digits
 */
export const VALID_POSTCODE_REGEX = /^\d{4}$/;

/**
 * Accept either local NZ formatting (e.g. 03 123 4567, 0800 123 456)
 * or the international-prefixed variant shown elsewhere in the UI (e.g. +64 3 123 4567, +64 03 123 4567).
 */
export const VALID_LABSYS_LANDLINE_PHONE_NUMBER_REGEX = /^(?:\+64 ?0?|0)(?:[34679] \d{3} \d{4}|(?:800|508) \d{3} \d{3,4})$/;

/**
 * Accept either local NZ mobile formatting (e.g. 021 123 4567)
 * or the international-prefixed variant (e.g. +64 21 123 4567 or +64 021 123 4567).
 */
export const VALID_LABSYS_MOBILE_PHONE_NUMBER_REGEX = /^(?:\+64 ?0?|0)2\d (\d{3} \d{3,4}|\d{4} \d{4})$/u;

/**
 * Numeric only
 */
export const VALID_PHONE_EXTENSION = /^\d*$/u;

/**
 * Converts a camelized string into all lower case separated by underscores.
 *
 ```javascript
 decamelize('innerHTML');         // 'inner_html'
 decamelize('action_name');       // 'action_name'
 decamelize('css-class-name');    // 'css-class-name'
 decamelize('my favorite items'); // 'my favorite items'
 ```
 */
export function decamelize(str: string): string {
    return str.replace(STRING_DECAMELIZE_REGEXP, '$1_$2').toLowerCase();
}

/**
 Replaces underscores, spaces, or camelCase with dashes.
 ```javascript
 dasherize('innerHTML');         // 'inner-html'
 dasherize('action_name');       // 'action-name'
 dasherize('css-class-name');    // 'css-class-name'
 dasherize('my favorite items'); // 'my-favorite-items'
 ```
 */
export function dasherize(str?: string): string {
    return decamelize(str || '').replace(STRING_DASHERIZE_REGEXP, '-');
}

/**
 Returns the lowerCamelCase form of a string.
 ```javascript
 camelize('innerHTML');          // 'innerHTML'
 camelize('action_name');        // 'actionName'
 camelize('css-class-name');     // 'cssClassName'
 camelize('my favorite items');  // 'myFavoriteItems'
 camelize('My Favorite Items');  // 'myFavoriteItems'
 ```
 */
export function camelize(str: string): string {
    return str
        .replace(
            STRING_CAMELIZE_REGEXP,
            (_match: string, _separator: string, chr: string) => chr ? chr.toUpperCase() : ''
        )
        .replace(/^([A-Z])/, (match: string) => match.toLowerCase());
}

/**
 Returns the UpperCamelCase form of a string.
 ```javascript
 'innerHTML'.classify();          // 'InnerHTML'
 'action_name'.classify();        // 'ActionName'
 'css-class-name'.classify();     // 'CssClassName'
 'my favorite items'.classify();  // 'MyFavoriteItems'
 ```
 */
export function classify(str: string): string {
    return str
        .split('.')
        .map(part => capitalize(camelize(part)))
        .join('.');
}

/**
 More general than decamelize. Returns the lower\_case\_and\_underscored
 form of a string.
 ```javascript
 'innerHTML'.underscore();          // 'inner_html'
 'action_name'.underscore();        // 'action_name'
 'css-class-name'.underscore();     // 'css_class_name'
 'my favorite items'.underscore();  // 'my_favorite_items'
 ```
 */
export function underscore(str: string): string {
    return str
        .replace(STRING_UNDERSCORE_REGEXP_1, '$1_$2')
        .replace(STRING_UNDERSCORE_REGEXP_2, '_')
        .toLowerCase();
}

/**
 Returns the Capitalized form of a string
 ```javascript
 'innerHTML'.capitalize()         // 'InnerHTML'
 'action_name'.capitalize()       // 'Action_name'
 'css-class-name'.capitalize()    // 'Css-class-name'
 'my favorite items'.capitalize() // 'My favorite items'
 ```
 */
export function capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.substr(1);
}

/**
 Returns the plural form of a string
 ```javascript
 'innerHTML'.pluralize()         // 'innerHTMLs'
 'action_name'.pluralize()       // 'actionNames'
 'css-class-name'.pluralize()    // 'cssClassNames'
 'regex'.pluralize()            // 'regexes'
 'user'.pluralize()             // 'users'
 ```
 */
export function pluralize(str: string): string {
    return camelize(
        [/([^aeiou])y$/, /()fe?$/, /([^aeiou]o|[sxz]|[cs]h)$/].map(
            (c, i) => (str = str.replace(c, `$1${'iv'[i] || ''}e`))
        ) && str + 's'
    );
}

export function getGroupName(name: string, group: string | undefined) {
    return group ? `${group}/${name}` : name;
}

export function featurePath(
    group: boolean | undefined,
    flat: boolean | undefined,
    path: string,
    name: string
) {
    if (group && !flat) {
        return `../../${path}/${name}/`;
    }

    return group ? `../${path}/` : './';
}

/** Returns if given string matches the given regex */
export function hasRegexPattern(string: string, pattern: RegExp): boolean {
    return !!string.match(pattern);
}

/**
 * Converts a camelCase string into a string that can be presented to the user.
 * @param camelCaseString - The camelCase string to convert
 * @param lowerCase - Whether to convert the string to lower case
 * @returns The presentable string, e.g. `'firstName'` becomes `'First Name'` or `'first name'` depending on `lowerCase`
 */
export const camelCaseToPresentable = (camelCaseString: string, lowerCase: boolean = true): string => {
    const prettified = camelCaseString.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
    return lowerCase ? prettified.toLowerCase() : prettified;
};
